import { useEffect, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { fetchMeetings, fetchMeetingsByClient } from "../services/meetings.service";
import { fetchClientsForMentions } from "../services/clients.service";

// MUI's own default filter (createFilterOptions()) was observed matching a
// large, inconsistent subset of a long (~700+) option list regardless of the
// typed query — e.g. typing a real client's name still surfaced unrelated
// entries, and swapping its `stringify` option didn't change that. Root cause
// not pinned down; a hand-written substring filter sidesteps it entirely and
// is simple enough to just trust.
function filterOptions(options: PickedMeeting[], state: { inputValue: string }): PickedMeeting[] {
  const query = state.inputValue.trim().toLowerCase();
  if (!query) return options.slice(0, 100);
  return options.filter((o) => o.label.toLowerCase().includes(query)).slice(0, 100);
}

export interface PickedMeeting {
  meetingId: string;
  clientId?: string;
  label: string;
}

/**
 * Lets an adviser pick a REAL meeting to run/test a workflow against, instead
 * of the trigger context being empty (a plain Run) or the fixed 'demo' stub
 * (the Simulate button). Without this, a workflow whose first step needs a
 * real meetingId/clientId — e.g. to ground a condition on an already-generated
 * file note — has nothing real to work with and fails immediately.
 */
export default function RunWithMeetingPicker({
  value,
  onChange,
  filterClientId,
}: {
  value: PickedMeeting | null;
  onChange: (meeting: PickedMeeting | null) => void;
  /** When set (a client already picked in the "Test variables" bar), only that client's own meetings are offered. */
  filterClientId?: string;
}) {
  const [options, setOptions] = useState<PickedMeeting[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // Once a client is already known, ask the server for just that client's
    // meetings instead of fetching the whole org and filtering client-side —
    // real scoping, not a bigger payload than necessary.
    const meetingsPromise = filterClientId ? fetchMeetingsByClient(filterClientId) : fetchMeetings();
    // Meeting titles are frequently just an auto-generated "Meeting on <date>"
    // shared by many unrelated meetings — the client name is the only real
    // disambiguator, so it's fetched alongside and merged into the label.
    Promise.all([meetingsPromise, fetchClientsForMentions().catch(() => [])])
      .then(([meetingsRes, clients]: [any, any]) => {
        if (cancelled) return;
        const clientNameById = new Map((clients || []).map((c: any) => [String(c.id), c.name]));
        const list = Array.isArray(meetingsRes) ? meetingsRes : meetingsRes?.meetings || meetingsRes?.data || [];
        setOptions(
          list.map((m: any) => {
            const clientId = m.clientid ? String(m.clientid) : undefined;
            const clientName = clientId ? clientNameById.get(clientId) : undefined;
            const titleAndDate = m.title ? `${m.title} — ${m.start?.date || ""}` : String(m._id || m.id);
            return {
              meetingId: m._id || m.id,
              clientId,
              label: clientName ? `${clientName} — ${titleAndDate}` : titleAndDate,
            };
          }),
        );
      })
      .catch(() => setOptions([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [filterClientId]);

  return (
    <Autocomplete
      size="small"
      loading={loading}
      options={options}
      value={value}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(o, v) => o.meetingId === v.meetingId}
      filterOptions={filterOptions}
      onChange={(_, next) => onChange(next)}
      sx={{ flex: 1, minWidth: 180 }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Test with meeting (optional)"
          placeholder={filterClientId ? "No meetings for this client" : "No meeting — run with empty context"}
          title={value?.label}
        />
      )}
    />
  );
}
